import type { DraftTrigger, Language } from '../../../shared/enums';
import { LANGUAGES } from '../../../shared/enums';
import { formatDateForLanguage, renderTemplate } from '../../../shared/templates';
import type { Template } from '../../../shared/types';
import { shell } from '../app';
import { NetworkError } from '../data/api';
import { listTemplates, updateTemplate } from '../data/drafts';
import { addDays, todayLocal } from '../logic/dates';
import { btn, empty, field, input, segmented, textarea } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { LANGUAGE_LABELS } from '../ui/labels';
import type { View } from '../ui/router';

/** Templates screen (M3): edit the trilingual copy per trigger; live preview with sample data,
 *  rendered by the SAME shared engine the Worker uses. */

const TRIGGER_TITLES: Record<DraftTrigger, string> = {
  contract_expiring: 'Contract expiring (auto, 60/30 days)',
  post_visit: 'Post-visit follow-up',
  meeting_confirm: 'Meeting confirmation',
  feature_launched: 'Feature launched (Watchlist, M4)',
};

const TRIGGER_ORDER: DraftTrigger[] = [
  'contract_expiring',
  'post_visit',
  'meeting_confirm',
  'feature_launched',
];

export const templatesView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Templates', { back: '#/emails' });
  const host = h('div', { class: 'page page--templates' });
  root.appendChild(host);

  let language: Language = 'en';
  let templates: Template[] = [];
  let disposed = false;
  /** Edits in progress per template: the cards are rebuilt on a language switch, and a peek at
   *  another translation must not throw away what was typed. A successful save clears the entry. */
  const edits = new Map<string, { subject: string; body: string }>();

  const sampleFields = (lang: Language) => ({
    owner_name: 'Ramón Ortega',
    business_name: 'Tacos Los Primos',
    contract_end_date: formatDateForLanguage(addDays(todayLocal(), 45), lang),
    feature_name: 'Nested modifiers',
  });

  const templateCard = (tpl: Template): HTMLElement => {
    let subject = edits.get(tpl.id)?.subject ?? tpl.subject;
    let body = edits.get(tpl.id)?.body ?? tpl.body;
    const remember = () => edits.set(tpl.id, { subject, body });
    const preview = h('div', { class: 'tpl__preview' });
    const renderPreview = () => {
      const fields = sampleFields(tpl.language);
      replaceChildren(
        preview,
        h('div', { class: 'tpl__previewsubject' }, renderTemplate(subject, fields)),
        h('pre', { class: 'tpl__previewbody' }, renderTemplate(body, fields)),
      );
    };
    renderPreview();
    const save = btn('Save', {
      variant: 'primary',
      size: 'sm',
      onClick: async () => {
        save.disabled = true;
        save.textContent = 'Saving…';
        try {
          const updated = await updateTemplate(tpl.id, { subject: subject.trim(), body: body.trim() });
          templates = templates.map((t) => (t.id === updated.id ? updated : t));
          edits.delete(tpl.id);
          shell.toast('Template saved', { kind: 'success' });
        } catch (err) {
          shell.toast(err instanceof Error ? err.message : String(err), { kind: 'error' });
        } finally {
          save.disabled = false;
          save.textContent = 'Save';
        }
      },
    });
    return h(
      'article',
      { class: 'tpl' },
      h(
        'h3',
        { class: 'tpl__title' },
        TRIGGER_TITLES[tpl.trigger],
        span('posbadge', LANGUAGE_LABELS[tpl.language]),
      ),
      field(
        'Subject',
        input({
          value: subject,
          maxlength: 200,
          onInput: (v) => {
            subject = v;
            remember();
            renderPreview();
          },
        }),
      ),
      field(
        'Body',
        textarea({
          value: body,
          rows: 9,
          maxlength: 8000,
          onInput: (v) => {
            body = v;
            remember();
            renderPreview();
          },
        }),
        { hint: 'Merge fields: {owner_name} {business_name} {contract_end_date} {feature_name}' },
      ),
      h('div', { class: 'tpl__previewlabel mono' }, 'PREVIEW · sample lead'),
      preview,
      h('div', { class: 'tpl__actions' }, save),
    );
  };

  const list = h('div', { class: 'tpls' }, empty('Loading templates…')); // painted until the answer
  const render = () => {
    const forLang = TRIGGER_ORDER.map((tr) =>
      templates.find((t) => t.trigger === tr && t.language === language),
    ).filter((t): t is Template => !!t);
    replaceChildren(
      list,
      forLang.length ? forLang.map(templateCard) : empty('No templates for this language.'),
    );
  };

  replaceChildren(
    host,
    field(
      'Language',
      segmented<Language>({
        options: LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABELS[l] })),
        value: language,
        onChange: (v) => {
          language = v;
          render();
        },
        columns: 3,
        size: 'md',
      }),
    ),
    list,
  );

  void (async () => {
    try {
      templates = await listTemplates();
      if (!disposed) render();
    } catch (err) {
      if (!disposed)
        replaceChildren(
          list,
          empty(
            err instanceof NetworkError ? 'Templates need a connection.' : 'Could not load templates.',
            err instanceof Error && !(err instanceof NetworkError) ? err.message : undefined,
          ),
        );
    }
  })();

  return () => {
    disposed = true;
  };
};
